import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const NewJobModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    designName: '',
    fabricMeters: '',
    priority: 'medium',
    dueDate: '',
    assignedOperator: '',
    instructions: '',
    sizeDistribution: {
      S: 0,
      M: 0,
      L: 0,
      XL: 0,
      '2XL': 0
    }
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const designOptions = [
    { value: '', label: 'Select Design' },
    { value: 'floral-summer', label: 'Floral Summer Collection' },
    { value: 'geometric-modern', label: 'Geometric Modern' },
    { value: 'vintage-classic', label: 'Vintage Classic' },
    { value: 'abstract-art', label: 'Abstract Art Series' },
    { value: 'nature-inspired', label: 'Nature Inspired' }
  ];

  const operatorOptions = [
    { value: '', label: 'Auto Assign' },
    { value: 'rajesh-kumar', label: 'Rajesh Kumar' },
    { value: 'priya-sharma', label: 'Priya Sharma' },
    { value: 'amit-patel', label: 'Amit Patel' },
    { value: 'sunita-devi', label: 'Sunita Devi' },
    { value: 'vikram-singh', label: 'Vikram Singh' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSizeChange = (size, value) => {
    setFormData(prev => ({
      ...prev,
      sizeDistribution: {
        ...prev.sizeDistribution,
        [size]: parseInt(value) || 0
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.designName) {
      newErrors.designName = 'Design name is required';
    }

    if (!formData.fabricMeters || formData.fabricMeters <= 0) {
      newErrors.fabricMeters = 'Valid fabric meters required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    const totalPieces = Object.values(formData.sizeDistribution).reduce((sum, count) => sum + count, 0);
    if (totalPieces === 0) {
      newErrors.sizeDistribution = 'At least one piece must be specified';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const totalPieces = Object.values(formData.sizeDistribution).reduce((sum, count) => sum + count, 0);
    const jobId = `JOB${Date.now().toString().slice(-6)}`;

    const newJob = {
      id: Date.now(),
      jobId,
      designName: formData.designName,
      fabricMeters: parseFloat(formData.fabricMeters),
      totalPieces,
      priority: formData.priority,
      dueDate: formData.dueDate,
      assignedOperator: formData.assignedOperator || 'Auto Assigned',
      instructions: formData.instructions,
      sizeDistribution: formData.sizeDistribution,
      progress: 0,
      status: 'pending',
      stage: 'Digital Printing',
      createdAt: new Date().toISOString(),
      defects: 0
    };

    onSave(newJob);
    onClose();
    
    // Reset form
    setFormData({
      designName: '',
      fabricMeters: '',
      priority: 'medium',
      dueDate: '',
      assignedOperator: '',
      instructions: '',
      sizeDistribution: {
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
        '2XL': 0
      }
    });
    setErrors({});
  };

  const getTotalPieces = () => {
    return Object.values(formData.sizeDistribution).reduce((sum, count) => sum + count, 0);
  };

  return (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001 p-4">
    <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
      {/* Modal Header */}
      <div className="flex-none p-6 border-b border-border flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Plus" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Create New Production Job
            </h2>
            <p className="text-sm text-muted-foreground">
              Add a new job to the production workflow
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          iconName="X"
          iconSize={20}
          onClick={onClose}
          aria-label="Close modal"
        />
      </div>

      {/* Scrollable Form Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Design Name"
              options={designOptions}
              value={formData.designName}
              onChange={(value) => handleInputChange('designName', value)}
              error={errors.designName}
              required
              searchable
            />
            <Input
              label="Fabric Required (meters)"
              type="number"
              value={formData.fabricMeters}
              onChange={(e) => handleInputChange('fabricMeters', e.target.value)}
              error={errors.fabricMeters}
              required
              min="0"
              step="0.1"
              placeholder="0.0"
            />
            <Select
              label="Priority"
              options={priorityOptions}
              value={formData.priority}
              onChange={(value) => handleInputChange('priority', value)}
            />
            <Input
              label="Due Date"
              type="date"
              value={formData.dueDate}
              onChange={(e) => handleInputChange('dueDate', e.target.value)}
              error={errors.dueDate}
              required
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Operator Assignment */}
          <div>
            <Select
              label="Assign Operator"
              description="Leave empty for automatic assignment based on workload"
              options={operatorOptions}
              value={formData.assignedOperator}
              onChange={(value) => handleInputChange('assignedOperator', value)}
              searchable
            />
          </div>

          {/* Size Distribution */}
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2">
              <h4 className="font-medium text-foreground">Size Distribution</h4>
              <div className="text-sm text-muted-foreground">
                Total:{' '}
                <span className="font-medium text-foreground">
                  {getTotalPieces()} pieces
                </span>
              </div>
            </div>
            {errors.sizeDistribution && (
              <p className="text-sm text-error mb-2">
                {errors.sizeDistribution}
              </p>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {Object.entries(formData.sizeDistribution).map(([size, count]) => (
                <div key={size}>
                  <Input
                    label={size}
                    type="number"
                    value={count}
                    onChange={(e) => handleSizeChange(size, e.target.value)}
                    min="0"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Special Instructions */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Special Instructions
            </label>
            <textarea
              className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
              value={formData.instructions}
              onChange={(e) => handleInputChange('instructions', e.target.value)}
              placeholder="Enter any special instructions for production..."
            />
          </div>

          {/* Summary Card */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3">Job Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Design:</span>
                <p className="font-medium text-foreground">
                  {formData.designName || 'Not selected'}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Fabric Required:</span>
                <p className="font-medium text-foreground">
                  {formData.fabricMeters || '0'} meters
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Pieces:</span>
                <p className="font-medium text-foreground">
                  {getTotalPieces()} pieces
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Priority:</span>
                <p
                  className={`font-medium capitalize ${
                    formData.priority === 'high'
                      ? 'text-error'
                      : formData.priority === 'medium'
                      ? 'text-warning'
                      : 'text-success'
                  }`}
                >
                  {formData.priority}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="flex-none flex items-center justify-end space-x-3 p-6 border-t border-border bg-card">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={handleSave}
          iconName="Plus"
          iconSize={16}
        >
          Create Job
        </Button>
      </div>
    </div>
  </div>
);

};

export default NewJobModal;